import { useMemo } from 'react'
import { useProjektprognosPerformance } from './usePerformance'
import { useProjektprognosPerformanceTotals } from './usePerformanceTotals'
import { usePeriodReportSnapshotData } from '../usePeriodReportSnapshotData'
import { usePeriodReportSnapshots } from '../usePeriodReportSnapshots'
import { useProjektprognosEditingContext } from '../../../context/ProjektprognosEditingContext'
import { getReportSnapshotIds } from '../../../utils/getReportSnapshotIds'
import { formatData } from '../../../../../utils/formatData'
import { GridRow } from '../../../../../interfaces/grids/GridRow'
import { GridRows } from '../../../../../interfaces/grids/GridRows'
import { AggTranslations } from '../../../../../interfaces/grids/performance/AggTranslation'

export const usePerformanceReportData = () => {
  const { selectedPeriodReport } = useProjektprognosEditingContext()

  const { data: periodReports, isSuccess: isPeriodReportsSuccess } =
    usePeriodReportSnapshots()
  const hasPeriodReports = (periodReports?.length ?? 0) > 0

  const { previousSnapshotId, currentSnapshotId } = useMemo(
    () => getReportSnapshotIds(selectedPeriodReport),
    [selectedPeriodReport]
  )

  const { previousSnapshot, currentSnapshot } = usePeriodReportSnapshotData(
    previousSnapshotId,
    currentSnapshotId
  )

  // Only fetch live data when: projectForecast exists, there is a previousSnapshot
  // to compare with, and there is no currentSnapshot (i.e. not viewing a closed report)
  const liveEnabled =
    isPeriodReportsSuccess &&
    hasPeriodReports &&
    !currentSnapshotId &&
    !!previousSnapshotId

  const livePerformance = useProjektprognosPerformance({ enabled: liveEnabled })
  const livePerformanceTotals = useProjektprognosPerformanceTotals({
    enabled: liveEnabled,
  })

  // Rows: use snapshot when in snapshot mode, live only when no current snapshot
  const rows = useMemo<GridRows | undefined>(() => {
    // Strict separation: snapshot data when currentSnapshotId exists, live otherwise
    const mainData = currentSnapshotId
      ? currentSnapshot.data?.performanceSheet?.data
        ? formatData(currentSnapshot.data.performanceSheet.data)
        : undefined
      : livePerformance.data

    if (!mainData) return undefined

    // Previous prognosis: always from previousSnapshot
    const previousSnapshotPerformance =
      previousSnapshot.data?.performanceSheet?.data
    if (
      !previousSnapshotPerformance ||
      previousSnapshotPerformance.length === 0
    ) {
      // No previous snapshot — set all differences to null
      const clearDifference = (row: GridRow): GridRow => ({
        ...row,
        difference: null,
      })

      return {
        prefixedData: mainData.prefixedData.map(clearDifference),
        emptyRowsHidden: mainData.emptyRowsHidden.map(clearDifference),
      }
    }

    // Format previous snapshot data for comparison
    const formattedSnapshot = formatData(previousSnapshotPerformance)

    // Create a Map for quick lookup based on accountId
    const snapshotMap = new Map<number, GridRow>()
    formattedSnapshot.prefixedData.forEach((row: GridRow) => {
      const accountId = row.accountId as number | undefined
      if (accountId) {
        snapshotMap.set(accountId, row)
      }
    })

    // Enrich a row with previous prognosis values
    const enrichRow = (row: GridRow): GridRow => {
      const enrichedRow: GridRow = { ...row, difference: null }

      const accountId = row.accountId as number | undefined
      if (!accountId) return enrichedRow

      const snapshotRow = snapshotMap.get(accountId)
      if (!snapshotRow) return enrichedRow

      const previousTotalPrognosis = snapshotRow.totalPrognosis as
        | number
        | null
        | undefined

      enrichedRow.previousTotalPrognosis = previousTotalPrognosis ?? null

      const currentPrognosis = row.totalPrognosis as number | null | undefined
      const hasCurrent =
        currentPrognosis !== undefined && currentPrognosis !== null
      const hasPrevious =
        previousTotalPrognosis !== undefined && previousTotalPrognosis !== null

      if (hasCurrent || hasPrevious) {
        const curr = Number(currentPrognosis ?? 0)
        const prev = Number(previousTotalPrognosis ?? 0)
        const isKostnader =
          (row.grandParentDisplayName as string)?.toUpperCase() === 'KOSTNADER'
        enrichedRow.difference = isKostnader ? prev - curr : curr - prev
      } else {
        enrichedRow.difference = null
      }

      return enrichedRow
    }

    const enrichedData: GridRows = {
      prefixedData: mainData.prefixedData.map(enrichRow),
      emptyRowsHidden: mainData.emptyRowsHidden.map(enrichRow),
    }

    return enrichedData
  }, [
    currentSnapshotId,
    livePerformance.data,
    previousSnapshot.data,
    currentSnapshot.data,
  ])

  // Totals: strict separation + enrich each group key with previousTotalPrognosis and computed difference
  const totals = useMemo<AggTranslations | undefined>(() => {
    const currentTotals = currentSnapshotId
      ? currentSnapshot.data?.performanceTotals
      : livePerformanceTotals.data

    if (!currentTotals) return undefined

    const previousTotals = previousSnapshot.data?.performanceTotals

    const enrichedTotals: AggTranslations = {}
    for (const key of Object.keys(currentTotals)) {
      const current = currentTotals[key]
      const previousTotalPrognosis =
        previousTotals?.[key]?.totalPrognosis ?? null
      const currentTotalPrognosis = current.totalPrognosis
      const hasCurrent =
        currentTotalPrognosis !== undefined && currentTotalPrognosis !== null
      const hasPrevious =
        previousTotalPrognosis !== undefined && previousTotalPrognosis !== null

      const isKostnader = key.toUpperCase() === 'KOSTNADER'
      const curr = Number(currentTotalPrognosis ?? 0)
      const prev = Number(previousTotalPrognosis ?? 0)
      enrichedTotals[key] = {
        ...current,
        previousTotalPrognosis,
        difference:
          hasCurrent || hasPrevious
            ? isKostnader
              ? prev - curr
              : curr - prev
            : null,
      }
    }

    return enrichedTotals
  }, [
    currentSnapshotId,
    currentSnapshot.data,
    livePerformanceTotals.data,
    previousSnapshot.data,
  ])

  // Previous snapshot name for column header
  const previousSnapshotName = useMemo<string | undefined>(() => {
    if (!selectedPeriodReport) return undefined

    const previousSnapshotRef =
      selectedPeriodReport.snapshotReferences?.previousSnapshotReference

    if (previousSnapshotRef) {
      return `${previousSnapshotRef.reportPeriod} ${previousSnapshotRef.year}`
    }

    return undefined
  }, [selectedPeriodReport])

  // Loading: combine previous + current (snapshot or live)
  const currentIsLoading = currentSnapshotId
    ? currentSnapshot.isLoading || currentSnapshot.isFetching
    : livePerformance.isLoading || livePerformanceTotals.isLoading

  const isLoading =
    previousSnapshot.isLoading ||
    previousSnapshot.isFetching ||
    currentIsLoading

  const error =
    previousSnapshot.error ||
    (currentSnapshotId ? currentSnapshot.error : livePerformance.error) ||
    null

  return {
    rows,
    totals,
    previousSnapshotName,
    isLoading,
    error,
  }
}
